import { useState } from 'react';
import { copilotApi, Source } from '../../api/copilot';
import {
  PlusIcon,
  XMarkIcon,
  DocumentIcon,
  CodeBracketIcon,
  GlobeAltIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface SourceManagementProps {
  sources: Source[];
  onSourceUpdate: () => void;
}

export default function SourceManagement({
  sources,
  onSourceUpdate,
}: SourceManagementProps) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [sourceType, setSourceType] = useState<'code' | 'url' | 'text'>('code');
  const [filePath, setFilePath] = useState('');
  const [url, setUrl] = useState('');
  const [textName, setTextName] = useState('');
  const [textContent, setTextContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [codeSearchResults, setCodeSearchResults] = useState<
    Array<{ path: string; language: string; size: number; snippet: string }>
  >([]);
  const [isSearching, setIsSearching] = useState(false);

  const handleAddSource = async () => {
    setIsLoading(true);
    try {
      switch (sourceType) {
        case 'code':
          if (!filePath.trim()) {
            toast.error('Please enter a file path');
            return;
          }
          await copilotApi.addCodeSource([filePath.trim()]);
          toast.success('Code source added');
          break;

        case 'url':
          if (!url.trim()) {
            toast.error('Please enter a URL');
            return;
          }
          await copilotApi.addUrlSource(url.trim());
          toast.success('URL source added');
          break;

        case 'text':
          if (!textName.trim() || !textContent.trim()) {
            toast.error('Please enter name and content');
            return;
          }
          await copilotApi.addTextSource(textName.trim(), textContent.trim());
          toast.success('Text source added');
          break;
      }

      setShowAddModal(false);
      resetForm();
      onSourceUpdate();
    } catch (error: any) {
      toast.error(error.message || 'Failed to add source');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveSource = async (id: string) => {
    try {
      await copilotApi.removeSource(id);
      toast.success('Source removed');
      onSourceUpdate();
    } catch (error: any) {
      toast.error('Failed to remove source');
    }
  };

  const handleSearchCodebase = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    try {
      const response = await copilotApi.searchCodebase(searchQuery.trim(), 10);
      setCodeSearchResults(response.results);
    } catch (error: any) {
      toast.error('Failed to search codebase');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectCodeFile = (path: string) => {
    setFilePath(path);
    setCodeSearchResults([]);
    setSearchQuery('');
  };

  const resetForm = () => {
    setFilePath('');
    setUrl('');
    setTextName('');
    setTextContent('');
    setSourceType('code');
    setSearchQuery('');
    setCodeSearchResults([]);
  };

  const getSourceIcon = (type: string) => {
    switch (type) {
      case 'code':
        return <CodeBracketIcon className="h-4 w-4" />;
      case 'url':
        return <GlobeAltIcon className="h-4 w-4" />;
      default:
        return <DocumentIcon className="h-4 w-4" />;
    }
  };

  const filteredSources = sources.filter((source) =>
    source.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    source.type.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-4">
      {/* Add Source Button */}
      <button
        onClick={() => setShowAddModal(true)}
        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 text-sm"
      >
        <PlusIcon className="h-5 w-5" />
        Add Source
      </button>

      {/* Sources List */}
      <div className="space-y-2">
        {filteredSources.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-4">
            No sources loaded. Add sources to provide context for BirdChatter.
          </p>
        ) : (
          filteredSources.map((source) => (
            <div
              key={source.id}
              className="flex items-start justify-between p-2 bg-slate-50/80 rounded border border-gray-200 group"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <div className="text-slate-500">
                    {getSourceIcon(source.type)}
                  </div>
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {source.name}
                  </p>
                </div>
                <p className="text-xs text-slate-500 capitalize">{source.type}</p>
              </div>
              <button
                onClick={() => handleRemoveSource(source.id)}
                className="opacity-0 group-hover:opacity-100 ml-2 text-slate-400 hover:text-red-600"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          ))
        )}
      </div>

      {/* Add Source Modal */}
      {showAddModal && (
        <div className="modal-overlay flex items-center justify-center z-50 p-4">
          <div className="modal-shell p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add Source</h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Source Type Selector */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Source Type
              </label>
              <div className="flex gap-2">
                {(['code', 'url', 'text'] as const).map((type) => (
                  <button
                    key={type}
                    onClick={() => setSourceType(type)}
                    className={`flex-1 px-3 py-2 rounded border ${
                      sourceType === type
                        ? 'bg-cyan-600 text-white border-blue-600'
                        : 'bg-white text-slate-700 border-gray-300 hover:bg-slate-50/80'
                    }`}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Code Source */}
            {sourceType === 'code' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    File Path
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={filePath}
                      onChange={(e) => setFilePath(e.target.value)}
                      placeholder="packages/api/src/modules/..."
                      className="flex-1 rounded border border-gray-300 px-3 py-2 text-sm"
                    />
                    <button
                      onClick={handleSearchCodebase}
                      className="px-3 py-2 bg-gray-100 text-slate-700 rounded hover:bg-gray-200"
                    >
                      <MagnifyingGlassIcon className="h-5 w-5" />
                    </button>
                  </div>
                  {codeSearchResults.length > 0 && (
                    <div className="mt-2 border border-gray-200 rounded max-h-40 overflow-y-auto">
                      {codeSearchResults.map((result, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleSelectCodeFile(result.path)}
                          className="w-full text-left px-3 py-2 hover:bg-slate-50/80 border-b border-gray-100 last:border-b-0"
                        >
                          <p className="text-sm font-medium text-slate-900">
                            {result.path}
                          </p>
                          <p className="text-xs text-slate-500">{result.language}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* URL Source */}
            {sourceType === 'url' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  URL
                </label>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                />
                <p className="text-xs text-slate-500 mt-1">
                  The URL content will be scraped automatically
                </p>
              </div>
            )}

            {/* Text Source */}
            {sourceType === 'text' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    value={textName}
                    onChange={(e) => setTextName(e.target.value)}
                    placeholder="Source name"
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Content
                  </label>
                  <textarea
                    value={textContent}
                    onChange={(e) => setTextContent(e.target.value)}
                    placeholder="Enter text content..."
                    rows={6}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-slate-50/80"
              >
                Cancel
              </button>
              <button
                onClick={handleAddSource}
                disabled={isLoading}
                className="flex-1 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50"
              >
                {isLoading ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

